import PageContainer from "../PageContainer";
import { FeatureGroupForm } from "./FeatureGroupForm";
import { useMemo, useState } from "react";
import {
  fetchFeatureGroups,
  editFeatureGroup,
} from "../../../services/featureGroupService";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { useData } from "../../../contexts/DataContext/DataContextt";
import { Link, Navigate, useParams } from "react-router-dom";
import { featureGroupsPageCache } from "../../../pages/cache/PageCache";
import { usePagedQuery } from "../../../contexts/hooks/usePagedQuery";
import { Stack, Tooltip, IconButton, Chip } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { localizeField } from "../../../config/localization";

const EditFeatureGroup = () => {
  const { language } = useData();
  const [updating, setUpdating] = useState(false);
  const { showNotification } = usePreload();
  const { id: featureGroupCode } = useParams();
  const {
    data: rows,
    fetched,
    isLoading,
  } = usePagedQuery({
    fetchFunction: fetchFeatureGroups,
    searchOption: [{ id: "featureGroupCode", display: "Code" }],
    cache: featureGroupsPageCache,
    language: "all",
    searchKey: featureGroupCode,
  });

  const row = useMemo(
    () => ({
      ...(rows[0] ?? {}),
      featureGroupName: localizeField(rows, "featureGroupName"),
      featureGroupDisplayName: localizeField(rows, "featureGroupDisplayName"),
    }),
    [rows],
  );

  if (fetched && !rows[0]?.featureGroupCode) {
    return <Navigate to="/notfound" replace />;
  }

  const onUpdate = async (updatedRow) => {
    try {
      setUpdating(true);
      const response = await editFeatureGroup(updatedRow);
      if (response instanceof Response && !response.ok) {
        throw new Error(
          `Edit feature group failed with status ${response.status}`,
        );
      }

      for (const [key, value] of [...featureGroupsPageCache.cache]) {
        let cacheLanguage;
        try {
          cacheLanguage = JSON.parse(key).language;
        } catch {
          cacheLanguage = key.startsWith(`${language}-`) ? language : undefined;
        }

        if (cacheLanguage !== language) {
          featureGroupsPageCache.cache.delete(key);
          continue;
        }

        const localizedRow = {
          ...updatedRow,
          featureGroupName: updatedRow.featureGroupName[cacheLanguage] ?? "",
          featureGroupDisplayName:
            updatedRow.featureGroupDisplayName[cacheLanguage] ?? "",
        };
        const data = value.data.map((item) =>
          item.featureGroupCode === updatedRow.featureGroupCode
            ? { ...item, ...localizedRow }
            : item,
        );
        featureGroupsPageCache.set(key, { ...value, data });
      }

      showNotification(`${updatedRow.featureGroupCode} was updated!`, "success");
      return true;
    } catch (error) {
      console.error(error);
      showNotification(
        `Something went wrong updating feature group ${updatedRow.featureGroupCode}.`,
        "error",
      );
      return false;
    } finally {
      setUpdating(false);
    }
  };

  return (
    <PageContainer
      title="Edit Feature Group"
      subtitle="Update the localized names for this ETIM feature group"
      titleAdornment={<Chip size="small" label={featureGroupCode} />}
      breadcrumbs={[
        { title: "ETIM Feature Groups", path: "/featuregroups" },
        { title: featureGroupCode },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip
            title="Back to feature groups"
            placement="bottom"
            enterDelay={300}
          >
            <span>
              <IconButton
                size="small"
                aria-label="Back to feature groups"
                component={Link}
                to="/featuregroups"
                disabled={updating}
              >
                <KeyboardBackspaceIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      <FeatureGroupForm
        onSave={onUpdate}
        props={{
          row,
          loading: isLoading,
          uploading: updating,
          saveText: "Save changes",
          isUpdate: true,
        }}
      />
    </PageContainer>
  );
};

export default EditFeatureGroup;
